import {
    type BoxShadowValue,
    type ImageStyle,
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import type {
    AvatarBackgroundColorConstantType,
    AvatarBadgeColorsConstantType,
    AvatarImageCachedConstantType,
    AvatarStatusColorConstantType,
    AvatarTitleAbbreviationTypes,
    AvatarRingColorConstantType,
    AvatarImageKeyConstantType,
    AvatarInitialsConstantType,
    AvatarSizeKeyConstantType,
    AvatarColorsConstantType,
    AvatarShadowConstantType,
    AvatarDynamicStyleType,
    AvatarSizeConstantType,
    AvatarTypeConstantType,
    AvatarStatusColorRole,
    AvatarMeasuresKeys,
    AvatarBadgeColors,
    AvatarSizeType,
    AvatarMeasures,
    AvatarColors,
    AvatarSize,
    AvatarType
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    ActivityStatusType,
    Mutable
} from "../../types";
import {
    getFocusRingStyle,
    STATE_COLOR_KEYS
} from "../../helpers/theme/state";
import {
    getSurfaceRelativeColor,
    getOpaqueColor,
    webStyle
} from "../../utils";
import {
    getHitArea
} from "../../helpers/theme/control";

export const AVATAR_SIZE_KEYS: Record<AvatarSizeType, AvatarSize> = {
    xxSmall: "xxSmall",
    xxxLarge: "xLarge",
    xxLarge: "xLarge",
    medium: "medium",
    xLarge: "xLarge",
    xSmall: "xSmall",
    small: "small",
    large: "large"
};

export const AVATAR_SIZES: Record<AvatarSize, AvatarMeasuresKeys> = {
    large: {
        fontSize: "headlineSmallSize",
        loadingSize: "large",
        size: "large"
    },
    xLarge: {
        fontSize: "displaySmallSize",
        loadingSize: "large",
        size: "xLarge"
    },
    xSmall: {
        fontSize: "labelMediumSize",
        loadingSize: "medium",
        size: "xSmall"
    },
    medium: {
        fontSize: "titleLargeSize",
        loadingSize: "large",
        size: "medium"
    },
    small: {
        fontSize: "labelLargeSize",
        loadingSize: "medium",
        size: "small"
    },
    xxSmall: {
        fontSize: "labelSmallSize",
        loadingSize: "small",
        size: "xxSmall"
    }
};

export const AVATAR_ABBREVIATION_LIMITS: Record<AvatarTitleAbbreviationTypes, number> = {
    "every-first": 3,
    "first-next": 2,
    "first-last": 2
};

export const AVATAR_BACKGROUND_TYPES: Partial<Record<keyof NCoreUIKit.ContainerContentColors, AvatarType>> = {
    neutral: "neutral",
    primary: "primary",
    success: "success",
    warning: "warning",
    danger: "danger",
    info: "info"
};

export const AVATAR_TYPE_ICON_COLORS: Record<AvatarType, keyof NCoreUIKit.IconContentColors> = {
    primary: "emphasized",
    neutral: "default",
    success: "success",
    warning: "warning",
    danger: "danger",
    info: "info"
};

export const AVATAR_STATUS_COLORS: Record<ActivityStatusType, AvatarStatusColorRole> = {
    away: {
        group: "system.default",
        key: "warning"
    },
    busy: {
        group: "system.default",
        key: "error"
    },
    idle: {
        group: "system.default",
        key: "info"
    },
    online: {
        group: "system.default",
        key: "success"
    },
    offline: {
        group: "content.icon",
        key: "low"
    }
};

export const AVATAR_STATUS_LABELS: Record<ActivityStatusType, keyof NCoreUIKit.Translation> = {
    offline: "activity-status-offline",
    online: "activity-status-online",
    away: "activity-status-away",
    busy: "activity-status-busy",
    idle: "activity-status-idle"
};

export const AVATAR_IMAGE_COMPONENT_KEY = "image-component";

export const AVATAR_WORD_PATTERN = /\s+/;

export const getAvatarSizeKey = ({
    size
}: AvatarSizeKeyConstantType): AvatarSize => {
    return AVATAR_SIZE_KEYS[size];
};

export const getAvatarSize = ({
    avatarSizes,
    iconSizes,
    borders,
    size
}: AvatarSizeConstantType): AvatarMeasures => {
    const currentSize = AVATAR_SIZES[getAvatarSizeKey({
        size
    })];

    const avatarSize = avatarSizes[currentSize.size];

    const statusIndicatorSize = Math.max(avatarSize / 4, borders.indicator * 2);

    const badgeSize = statusIndicatorSize + borders.subtract * 2;

    return {
        loadingSize: iconSizes[currentSize.loadingSize],
        badgeIconSize: Math.round(badgeSize * 3 / 4),
        fontSize: currentSize.fontSize,
        iconSize: avatarSize / 2,
        statusIndicatorSize,
        size: avatarSize,
        badgeSize
    };
};

export const getAvatarInitials = ({
    titleAbbreviationType,
    locale,
    title
}: AvatarInitialsConstantType): string => {
    if(!title) {
        return "";
    }

    const firstLetters = title.trim().split(AVATAR_WORD_PATTERN).filter((word) => word.length > 0).map((word) => Array.from(word)[0] ?? "");

    const letters = titleAbbreviationType === "first-last" ? [
        ...firstLetters.slice(0, 1),
        ...firstLetters.slice(1).slice(-1)
    ] : firstLetters.slice(0, AVATAR_ABBREVIATION_LIMITS[titleAbbreviationType]);

    const initials = letters.join("");

    try {
        return initials.toLocaleUpperCase(locale);
    } catch {
        return initials.toUpperCase();
    }
};

export const getAvatarImageKey = ({
    imageSource,
    imageUrl,
    image
}: AvatarImageKeyConstantType): string | undefined => {
    if(image) {
        return AVATAR_IMAGE_COMPONENT_KEY;
    }

    return imageSource?.uri ?? imageUrl;
};

export const getAvatarImageCached = ({
    imageKey
}: AvatarImageCachedConstantType): boolean => {
    if(!imageKey || imageKey === AVATAR_IMAGE_COMPONENT_KEY || Platform.OS !== "web" || typeof globalThis.Image !== "function") {
        return false;
    }

    const probe = new globalThis.Image();

    probe.src = imageKey;

    return probe.complete && probe.naturalWidth > 0;
};

export const getAvatarType = ({
    backgroundColor,
    type
}: AvatarTypeConstantType): AvatarType => {
    if(!backgroundColor || backgroundColor === "transparent") {
        return type;
    }

    return AVATAR_BACKGROUND_TYPES[backgroundColor] ?? type;
};

export const getAvatarBackgroundColor = ({
    backgroundColor,
    colors
}: AvatarBackgroundColorConstantType): string | undefined => {
    if(!backgroundColor) {
        return undefined;
    }

    if(backgroundColor === "transparent") {
        return backgroundColor;
    }

    if(AVATAR_BACKGROUND_TYPES[backgroundColor]) {
        return undefined;
    }

    return colors.content.container[backgroundColor];
};

export const getAvatarColors = ({
    backgroundColor,
    isPlaceholder,
    surfaceColor,
    isDisabled,
    titleColor,
    iconColor,
    colors,
    type
}: AvatarColorsConstantType): AvatarColors => {
    if(isDisabled) {
        return {
            hairlineColor: colors.system.state.border.disabled.neutral,
            titleColor: colors.system.state.content.disabled.neutral,
            containerColor: getOpaqueColor({
                color: colors.system.state.overlay.disabled.neutral,
                surface: colors.content.container[surfaceColor]
            }),
            iconColor: colors.system.state.content.disabled.neutral,
            iconColorKey: "disabled"
        };
    }

    const currentType = getAvatarType({
        backgroundColor,
        type
    });

    const rawBackgroundColor = getAvatarBackgroundColor({
        backgroundColor,
        colors
    });

    const stateColorKey = STATE_COLOR_KEYS[currentType];

    const currentColors: AvatarColors = {
        titleColor: colors.system.onContainer[stateColorKey],
        containerColor: getSurfaceRelativeColor({
            surface: colors.content.container[surfaceColor],
            color: colors.system.container[stateColorKey],
            ground: colors.content.container.default
        }),
        iconColor: colors.system.onContainer[stateColorKey],
        iconColorKey: AVATAR_TYPE_ICON_COLORS[currentType]
    };

    if(rawBackgroundColor) {
        currentColors.containerColor = rawBackgroundColor;
    }

    if(currentType === "neutral" && (backgroundColor === "subtle" || (!rawBackgroundColor && isPlaceholder))) {
        currentColors.hairlineColor = colors.content.border.subtle;
    }

    if(titleColor) {
        currentColors.titleColor = colors.content.text[titleColor];
    }

    if(iconColor) {
        const _iconColor = iconColor as keyof NCoreUIKit.ProjectColorPalette;

        currentColors.iconColor = colors.project[_iconColor] ?? iconColor;
    }

    return currentColors;
};

export const getAvatarRingColor = ({
    avatarBackgroundColor,
    borderColor,
    colors
}: AvatarRingColorConstantType): string => {
    if(!borderColor) {
        return colors.content.container[avatarBackgroundColor];
    }

    if(borderColor === "transparent") {
        return borderColor;
    }

    return colors.content.border[borderColor];
};

export const getAvatarStatusColor = ({
    statusIndicatorType,
    colors
}: AvatarStatusColorConstantType): string => {
    const currentRole = AVATAR_STATUS_COLORS[statusIndicatorType];

    if(currentRole.group === "content.border") {
        return colors.content.border[currentRole.key];
    }

    if(currentRole.group === "content.icon") {
        return colors.content.icon[currentRole.key];
    }

    return colors.system.default[currentRole.key];
};

export const getAvatarBadgeColors = ({
    isDisabled,
    colors,
    badge
}: AvatarBadgeColorsConstantType): AvatarBadgeColors => {
    const typeColors = getTypeColors({
        type: badge?.type ?? "primary",
        variant: "filled",
        isDisabled,
        colors
    });

    return {
        containerColor: typeColors.containerColor,
        iconColor: typeColors.iconColor
    };
};

export const getAvatarShadow = ({
    hairlineColor,
    ringColor,
    borders
}: AvatarShadowConstantType): Array<BoxShadowValue> => {
    if(ringColor) {
        return [
            {
                spreadDistance: borders.subtract,
                color: ringColor,
                offsetX: 0,
                offsetY: 0
            }
        ];
    }

    if(!hairlineColor) {
        return [];
    }

    return [
        {
            spreadDistance: borders.line,
            color: hairlineColor,
            inset: true,
            offsetX: 0,
            offsetY: 0
        }
    ];
};

const stylesheet = StyleSheet.create({
    fallback: {
        ...StyleSheet.absoluteFill,
        justifyContent: "center",
        alignItems: "center"
    },
    container: {
        justifyContent: "center",
        alignItems: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    surface: {
        justifyContent: "center",
        position: "relative",
        alignItems: "center",
        overflow: "visible"
    },
    badge: {
        justifyContent: "center",
        position: "absolute",
        alignItems: "center",
        bottom: 0,
        end: 0
    },
    statusIndicator: {
        position: "absolute",
        bottom: 0,
        end: 0
    },
    title: {
        textAlign: "center"
    },
    image: {
        objectFit: "cover"
    }
});

export const useStyles = ({
    isShowStatusIndicatorBorder,
    isFocusVisible,
    currentColors,
    isActionable,
    touchTargets,
    currentSize,
    pointerType,
    statusColor,
    badgeColor,
    floorColor,
    isLoading,
    ringColor,
    radiuses,
    borders,
    colors
}: AvatarDynamicStyleType) => {
    const hitArea = getHitArea({
        visualSize: currentSize.size,
        touchTargets,
        pointerType
    });

    const surfaceShadow = getAvatarShadow({
        hairlineColor: currentColors.hairlineColor,
        ringColor,
        borders
    });

    const styles = {
        surface: {
            backgroundColor: currentColors.containerColor,
            borderRadius: radiuses.full,
            height: currentSize.size,
            width: currentSize.size
        } as Mutable<ViewStyle>,
        badge: {
            backgroundColor: badgeColor.containerColor,
            height: currentSize.badgeSize,
            width: currentSize.badgeSize,
            borderRadius: radiuses.full
        } as Mutable<ViewStyle>,
        statusIndicator: {
            height: currentSize.statusIndicatorSize,
            width: currentSize.statusIndicatorSize,
            borderRadius: radiuses.full,
            backgroundColor: statusColor
        } as Mutable<ViewStyle>,
        image: {
            borderRadius: radiuses.full,
            height: currentSize.size,
            width: currentSize.size
        } as Mutable<ImageStyle>,
        container: {
            height: hitArea.minHeight,
            width: hitArea.minWidth
        } as Mutable<ViewStyle>
    };

    if(surfaceShadow.length) {
        styles.surface.boxShadow = surfaceShadow;
    }

    if(isShowStatusIndicatorBorder) {
        styles.statusIndicator.boxShadow = getAvatarShadow({
            ringColor: floorColor,
            borders
        });

        styles.badge.boxShadow = getAvatarShadow({
            ringColor: floorColor,
            borders
        });
    }

    if(isActionable) {
        Object.assign(styles.surface, getFocusRingStyle({
            colorKey: "default",
            isFocusVisible,
            borders,
            colors
        }));
    }

    if(isActionable && isLoading) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    return styles;
};
export default stylesheet;
